import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useUploadMovie } from '../hooks/useMovie';
import { movieApi } from '../api/client';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { checkImageRatio, POSTER_SPEC, THUMBNAIL_SPEC } from '../lib/image-ratio';
import { cn } from '../lib/utils';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, required, hint, icon, children }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon && <Icon icon={icon} className="size-3.5" />}
        {label} {required && <span className="text-destructive">*</span>}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

// Common age/content ratings across the scales a Nigerian streaming catalogue is likely to
// need — NFVCB (Nigeria's own classification body) plus the widely-recognized MPA scale.
const RATING_OPTIONS = ['G', 'PG', 'PG-13', 'R18', '12', '12A', '15', '18', 'NR'];
const CLASSIFICATION_AUTHORITY_OPTIONS = ['NFVCB', 'MPA', 'BBFC', 'Other'];
// Selectable defaults — not an exhaustive list; the free-text field below covers anything else.
const CONTENT_DESCRIPTOR_OPTIONS = [
  'Violence', 'Strong Violence', 'Language', 'Sexual Content', 'Nudity',
  'Drugs', 'Alcohol', 'Horror', 'Frightening Scenes',
];

function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await movieApi.getCategories();
      if (!data.success) throw new Error(data.message || 'Failed to load categories');
      return data.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
}

function PreviewCard({ form, thumbPreview, posterPreview, categoryNames, isKidsContent, hasTrailer, hasVideoFile }) {
  // Rows and grids show the portrait poster; with no poster they fall back to a centre-crop of the thumbnail.
  const art = posterPreview || thumbPreview;
  return (
    <div className="sticky top-6 mt-10 hidden shrink-0 lg:block lg:w-72 xl:w-80">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        Preview <span className="font-normal text-muted-foreground/60">(as it appears in rows)</span>
      </p>
      <div className="overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10">
        <div className="relative aspect-[2/3] w-full overflow-hidden bg-white/[0.03]">
          {art ? (
            <img src={art} alt="" className="size-full object-cover" />
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-1.5 text-muted-foreground/40">
              <Icon icon="ph:image-bold" className="size-8" />
              <span className="text-[11px]">No poster yet</span>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute top-2 right-2 flex gap-1">
            {hasTrailer && (
              <Badge variant="secondary" className="gap-1 bg-black/50 text-white backdrop-blur-sm">
                <Icon icon="ph:film-slate-bold" className="size-3" /> Trailer
              </Badge>
            )}
            {hasVideoFile && (
              <Badge variant="secondary" className="gap-1 bg-black/50 text-white backdrop-blur-sm">
                <Icon icon="ph:play-fill" className="size-3" /> Full movie
              </Badge>
            )}
          </div>
          <div className="absolute inset-x-0 bottom-0 p-3">
            <h3 className="line-clamp-2 text-sm font-semibold text-white drop-shadow-sm">
              {form.title.trim() || 'Untitled movie'}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-white/70">
              {form.releaseYear && <span>{form.releaseYear}</span>}
              {form.director && (
                <>
                  {form.releaseYear && <span>·</span>}
                  <span className="truncate">Dir. {form.director}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 p-3.5">
          {categoryNames?.length > 0 && (
            <Badge variant="outline" className="w-fit">{categoryNames.join(' · ')}</Badge>
          )}
          <p className="line-clamp-4 text-xs leading-relaxed text-muted-foreground">
            {form.description.trim() || 'A description will appear here as you type.'}
          </p>
          {form.cast && (
            <p className="line-clamp-2 text-[11px] text-muted-foreground/70">
              <span className="text-muted-foreground/50">Cast: </span>{form.cast}
            </p>
          )}
          {isKidsContent && (
            <Badge variant="secondary" className="w-fit gap-1">
              <Icon icon="ph:baby-bold" className="size-3" /> Kids friendly
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UploadMoviePage() {
  usePageTitle('Upload Movie · Kast');
  const { mutateAsync, isPending } = useUploadMovie();
  const { data: categories = [], error: categoriesError } = useCategories();

  const [form, setForm] = useState({
    title: '', description: '', director: '', cast: '', releaseYear: '', categoryIds: [],
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [trailer, setTrailer] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Parental controls: only titles tagged here are ever visible/playable on a Kids profile
  // (see Movie.IsKidsContent). Defaults off — kids-appropriateness is an opt-in judgment call
  // by the uploader, reviewed again by an admin before the title goes live either way.
  const [isKidsContent, setIsKidsContent] = useState(false);

  const thumbPreviewUrl = useRef(null);
  const posterPreviewUrl = useRef(null);
  useEffect(() => () => {
    if (thumbPreviewUrl.current) URL.revokeObjectURL(thumbPreviewUrl.current);
    if (posterPreviewUrl.current) URL.revokeObjectURL(posterPreviewUrl.current);
  }, []);

  // ── Content rating ────────────────────────────────────────────────────
  const [rating, setRating] = useState('');
  const [classificationAuthority, setClassificationAuthority] = useState('NFVCB');
  const [descriptors, setDescriptors] = useState([]);
  const [customDescriptors, setCustomDescriptors] = useState('');

  const toggleDescriptor = (d) => {
    setDescriptors((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleThumb = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnail(file);
    checkImageRatio(file, THUMBNAIL_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    if (thumbPreviewUrl.current) URL.revokeObjectURL(thumbPreviewUrl.current);
    const url = URL.createObjectURL(file);
    thumbPreviewUrl.current = url;
    setThumbPreview(url);
  };

  const handlePoster = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPoster(file);
    checkImageRatio(file, POSTER_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    if (posterPreviewUrl.current) URL.revokeObjectURL(posterPreviewUrl.current);
    const url = URL.createObjectURL(file);
    posterPreviewUrl.current = url;
    setPosterPreview(url);
  };

  const categoryNames = categories
    .filter((c) => form.categoryIds.includes(String(c.Id || c.id)))
    .map((c) => c.Name || c.name);

  const toggleCategory = (id) => setForm((f) => ({
    ...f,
    categoryIds: f.categoryIds.includes(id) ? f.categoryIds.filter((value) => value !== id) : [...f.categoryIds, id],
  }));

  const resetForm = () => {
    setForm({ title: '', description: '', director: '', cast: '', releaseYear: '', categoryIds: [] });
    setThumbnail(null);
    setTrailer(null);
    setVideoFile(null);
    if (thumbPreviewUrl.current) URL.revokeObjectURL(thumbPreviewUrl.current);
    thumbPreviewUrl.current = null;
    setThumbPreview(null);
    setPoster(null);
    if (posterPreviewUrl.current) URL.revokeObjectURL(posterPreviewUrl.current);
    posterPreviewUrl.current = null;
    setPosterPreview(null);
    setIsKidsContent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!trailer) { toast.error('Trailer video is required'); return; }

    const fd = new FormData();
    fd.append('Title', form.title.trim());
    if (form.description) fd.append('Description', form.description);
    if (form.director) fd.append('Director', form.director);
    if (form.cast) fd.append('Cast', form.cast);
    if (form.releaseYear) fd.append('ReleaseYear', form.releaseYear);
    form.categoryIds.forEach((id) => fd.append('CategoryIds', id));
    if (thumbnail) fd.append('Thumbnail', thumbnail);
    if (poster) fd.append('Poster', poster);
    if (trailer) fd.append('Trailer', trailer);
    if (videoFile) fd.append('VideoFile', videoFile);
    fd.append('IsKidsContent', isKidsContent);

    if (rating) fd.append('Rating', rating);
    if (classificationAuthority) fd.append('ClassificationAuthority', classificationAuthority);
    const allDescriptors = [...descriptors, ...customDescriptors.split(',').map((s) => s.trim()).filter(Boolean)];
    if (allDescriptors.length) fd.append('ContentDescriptors', allDescriptors.join(','));

    setUploadProgress(0);
    try {
      const { data } = await mutateAsync({
        formData: fd,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      if (data.success) {
        toast.success('Movie uploaded! Pending admin approval.');
        resetForm();
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Upload failed'));
    } finally {
      setUploadProgress(0);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
          <Icon icon="ph:film-slate-bold" className="size-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Upload Movie</h1>
          <p className="text-xs text-muted-foreground">Your upload will be reviewed by an admin before it goes live</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <form
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          className="flex flex-1 flex-col gap-6 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] sm:p-6"
        >
          <Field label="Title" required>
            <input name="title" className={inputClass} value={form.title} onChange={handleChange} placeholder="Movie title" required />
          </Field>

          <Field label="Description">
            <textarea
              name="description"
              className={`${inputClass} resize-none`}
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Short description…"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Director">
              <input name="director" className={inputClass} value={form.director} onChange={handleChange} placeholder="Director name" />
            </Field>
            <Field label="Release Year">
              <input
                name="releaseYear"
                type="number"
                className={inputClass}
                value={form.releaseYear}
                onChange={handleChange}
                placeholder="e.g. 2024"
                min="1900"
                max="2099"
              />
            </Field>
          </div>

          <Field label="Cast" hint="(comma-separated)">
            <input name="cast" className={inputClass} value={form.cast} onChange={handleChange} placeholder="Actor A, Actor B, Actor C" />
          </Field>

          <Field label="Categories" hint="(select all that apply; first selection is primary)">
            {categoriesError && (
              <p className="mb-1.5 text-xs text-destructive">{getApiErrorMessage(categoriesError, 'Failed to load categories.')}</p>
            )}
            <div className="flex flex-wrap gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3">
              {categories.map((c) => {
                const id = String(c.Id || c.id); const selected = form.categoryIds.includes(id);
                return <label key={id} className={cn('cursor-pointer rounded-full border px-3 py-1.5 text-xs transition-colors', selected ? 'border-primary/60 bg-primary/15 text-primary' : 'border-white/15 text-muted-foreground hover:border-white/30')}>
                  <input type="checkbox" className="sr-only" checked={selected} onChange={() => toggleCategory(id)} />
                  {c.Name || c.name}
                </label>;
              })}
              {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories found.</span>}
            </div>
          </Field>

          <Field label="Parental Guidance" icon="ph:baby-bold">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-3 transition-colors hover:bg-white/[0.04]">
              <input
                type="checkbox"
                checked={isKidsContent}
                onChange={(e) => setIsKidsContent(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-primary"
              />
              <span>
                <strong className="text-sm font-medium text-foreground">Safe for Kids profiles</strong>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Tag this title as appropriate for a Kids profile. Only content tagged here will ever
                  appear on, or be playable from, a Kids profile — everything else is restricted to
                  Adult profiles. You (or an admin) can change this later.
                </p>
              </span>
            </label>
          </Field>

          <Field label="Content Rating" icon="ph:shield-check-bold">
            <div className="grid grid-cols-2 gap-4">
              <Select value={rating} onValueChange={setRating}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="— Select rating —" />
                </SelectTrigger>
                <SelectContent>
                  {RATING_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={classificationAuthority} onValueChange={setClassificationAuthority}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_AUTHORITY_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <p className="mt-3 mb-1.5 text-xs font-medium text-muted-foreground">
              Content descriptors <span className="font-normal text-muted-foreground/60">(select any that apply)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {CONTENT_DESCRIPTOR_OPTIONS.map((d) => {
                const active = descriptors.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggleDescriptor(d)}
                    className={cn(
                      'rounded-full border px-3.5 py-1.5 text-xs transition-colors',
                      active
                        ? 'border-primary/60 bg-primary/15 text-primary'
                        : 'border-white/15 bg-white/[0.02] text-muted-foreground hover:border-white/30'
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            <input
              className={`${inputClass} mt-2`}
              placeholder="Other descriptors (comma-separated) — not limited to the list above"
              value={customDescriptors}
              onChange={(e) => setCustomDescriptors(e.target.value)}
            />
          </Field>

          <Field label="Poster Image" icon="ph:image-bold" hint="(portrait 2:3, e.g. 1000×1500 — shown in rows & grids)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:image-bold" className="size-4 shrink-0" />
              <span className="truncate">{poster ? poster.name : 'Choose portrait poster…'}</span>
              <input type="file" accept="image/*" onChange={handlePoster} className="hidden" />
            </label>
          </Field>

          <Field label="Thumbnail Image" icon="ph:image-bold" hint="(landscape 16:9, e.g. 1920×1080 — hero banner & title page)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:image-bold" className="size-4 shrink-0" />
              <span className="truncate">{thumbnail ? thumbnail.name : 'Choose image…'}</span>
              <input type="file" accept="image/*" onChange={handleThumb} className="hidden" />
            </label>
          </Field>

          <Field label="Trailer / Intro Video" required icon="ph:film-slate-bold">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:film-slate-bold" className="size-4 shrink-0" />
              <span className="truncate">{trailer ? trailer.name : 'Choose trailer video…'}</span>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setTrailer(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </Field>

          <Field label="Movie Video File" icon="ph:video-camera-bold" hint="(optional)">
            <p className="mb-1.5 text-xs text-muted-foreground">
              For a single full-length movie, upload the feature here — no separate episode step needed.
              Leave this empty if you're uploading a multi-episode series (use "Upload Episode" after this,
              from the Creator Dashboard).
            </p>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:video-camera-bold" className="size-4 shrink-0" />
              <span className="truncate">{videoFile ? videoFile.name : 'Choose movie video…'}</span>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </Field>

          <Button type="submit" disabled={isPending} className="mt-1 gap-1.5 self-start">
            {isPending ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Icon icon="ph:upload-simple-bold" />
                Upload Movie
              </>
            )}
          </Button>

          {isPending && (
            <div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Finishing up…'}
              </p>
            </div>
          )}
        </form>

        <PreviewCard
          form={form}
          thumbPreview={thumbPreview}
          posterPreview={posterPreview}
          categoryNames={categoryNames}
          isKidsContent={isKidsContent}
          hasTrailer={!!trailer}
          hasVideoFile={!!videoFile}
        />
      </div>
    </div>
  );
}
